
import { Portfolio, PortfolioTeamMember, UserProfile } from '../../types';
import { updateUserProfile } from './users';
import { synczbase } from '../../lib/synczbase';
import { uploadDeliveryFile } from './files';

// --- PORTFOLIO PROFILE ---

export const getPortfolioByOwner = async (ownerId: string): Promise<Portfolio | null> => {
    const snap = await synczbase.query("SELECT * FROM portfolios WHERE \"ownerId\" = $1 LIMIT 1", [ownerId]);
    if (snap && snap.length > 0) {
        const row = snap[0];
        let dataObj: any = {};
        if (row.data) {
            try {
                dataObj = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
            } catch(e) {}
        }
        // Exclude null/undefined properties from SQL row so they do NOT overwrite non-null properties in JSON dataObj
        const cleanRow: any = {};
        for (const k in row) {
            if (row[k] !== null && row[k] !== undefined && k !== 'data') {
                cleanRow[k] = row[k];
            }
        }
        // JSON dataObj has priority for user-editable fields if present, backed by cleanRow for standard DB columns
        const publicData = { ...cleanRow, ...dataObj } as Portfolio;
        
        try {
            const secretSnap = await synczbase.query("SELECT * FROM portfolio_secrets WHERE \"portfolioId\" = $1 LIMIT 1", [publicData.id]);
            if (secretSnap && secretSnap.length > 0) {
                const { smtpPassword, whatsappApiKey, ...safeSecrets } = secretSnap[0] as any;
                return { ...publicData, ...safeSecrets };
            }
        } catch(e) {
            // Ignore permission errors
        }
        
        return publicData;
    }
    return null;
};

export const createPortfolio = async (portfolio: Omit<Portfolio, 'id'>) => {
    const id = `port_${Date.now()}_${Math.random().toString(36).substring(2,9)}`;
    const fullData = { ...portfolio, id };
    
    try {
        await synczbase.query(
            `INSERT INTO portfolios (
                id, name, "ownerId", "contactEmail", "contactPhone", address, timezone, 
                "heroVideoUrl", "servicesGridCols", "logoUrl", "syncProfileToSettings", description,
                "seoTitle", "seoDescription", "seoKeywords", "trustedBrands", "trustedBrandsTitle", "trustedBrandsLayout", "trustedBrandsSize",
                data
            ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14, $15, $16, $17, $18, $19, $20)`,
            [
                id, 
                portfolio.name || null, 
                portfolio.ownerId || null, 
                portfolio.contactEmail || null,
                portfolio.contactPhone || null,
                portfolio.address || null,
                portfolio.timezone || 'Europe/London',
                portfolio.heroVideoUrl || null,
                portfolio.servicesGridCols || 3,
                portfolio.logoUrl || null,
                portfolio.syncProfileToSettings ?? false,
                portfolio.description || null,
                portfolio.seoTitle || null,
                portfolio.seoDescription || null,
                portfolio.seoKeywords || null,
                portfolio.trustedBrands ? JSON.stringify(portfolio.trustedBrands) : null,
                portfolio.trustedBrandsTitle || null,
                portfolio.trustedBrandsLayout || 'GRID',
                portfolio.trustedBrandsSize || 'MEDIUM',
                JSON.stringify(fullData)
            ]
        );
    } catch(e) {
        await synczbase.query(
            "INSERT INTO portfolios (id, data) VALUES ($1, $2)",
            [id, JSON.stringify(fullData)]
        );
    }
    return id;
};

export const updatePortfolio = async (id: string, data: Partial<Portfolio>) => {
    const { smtpPassword, whatsappApiKey, ...publicData } = data;
    
    try {
        const snap = await synczbase.query("SELECT * FROM portfolios WHERE id = $1 LIMIT 1", [id]);
        let currentData: any = {};
        let name = publicData.name;
        let ownerId = publicData.ownerId;
        
        if (snap && snap.length > 0) {
            const row = snap[0];
            if (!ownerId) ownerId = row.ownerId;
            if (!name) name = row.name || row.title;
            if (row.data) {
                try {
                    currentData = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
                } catch(e) {}
            }
        }
        const merged = { ...currentData, ...publicData };
        const jsonStr = JSON.stringify(merged);
        
        try {
            await synczbase.query(
                `UPDATE portfolios SET 
                    data = $1, 
                    name = COALESCE($2, name), 
                    "ownerId" = COALESCE($3, "ownerId"),
                    "contactEmail" = $4,
                    "contactPhone" = $5,
                    address = $6,
                    timezone = $7,
                    "heroVideoUrl" = $8,
                    "servicesGridCols" = $9,
                    "logoUrl" = COALESCE($10, "logoUrl"),
                    "syncProfileToSettings" = $11,
                    description = COALESCE($12, description),
                    "seoTitle" = $13,
                    "seoDescription" = $14,
                    "seoKeywords" = $15,
                    "trustedBrands" = $16,
                    "trustedBrandsTitle" = $17,
                    "trustedBrandsLayout" = $18,
                    "trustedBrandsSize" = $19
                 WHERE id = $20`,
                [
                    jsonStr, 
                    name || null, 
                    ownerId || null, 
                    merged.contactEmail || null, 
                    merged.contactPhone || null, 
                    merged.address || null, 
                    merged.timezone || 'Europe/London', 
                    merged.heroVideoUrl || null, 
                    merged.servicesGridCols || 3, 
                    merged.logoUrl || null,
                    merged.syncProfileToSettings ?? false,
                    merged.description || null,
                    merged.seoTitle || null,
                    merged.seoDescription || null,
                    merged.seoKeywords || null,
                    merged.trustedBrands ? (typeof merged.trustedBrands === 'string' ? merged.trustedBrands : JSON.stringify(merged.trustedBrands)) : null,
                    merged.trustedBrandsTitle || null,
                    merged.trustedBrandsLayout || 'GRID',
                    merged.trustedBrandsSize || 'MEDIUM',
                    id
                ]
            );
        } catch(sqlErr) {
            await synczbase.query(
                "UPDATE portfolios SET data = $1 WHERE id = $2",
                [jsonStr, id]
            );
        }
    } catch(e) {
        console.error("Failed to update portfolio:", e);
    }
    
    if (smtpPassword || whatsappApiKey || data.smtpHost || data.smtpEmail || data.whatsappApiUrl) {
        await savePortfolioSecrets(id, {
            smtpPassword,
            whatsappApiKey,
            smtpHost: data.smtpHost,
            smtpPort: data.smtpPort,
            smtpEmail: data.smtpEmail,
            whatsappApiUrl: data.whatsappApiUrl
        });
    }
};

export const savePortfolioSecrets = async (portfolioId: string, secrets: any) => {
    try {
        await synczbase.apiCall('/api/functions/savePortfolioSecrets', 'POST', { portfolioId, ...secrets });
    } catch(e) {
        console.warn("API Function missing, falling back to direct db write (requires admin role)");
        // Fallback for demo simplicity
        const snap = await synczbase.query("SELECT id FROM portfolio_secrets WHERE \"portfolioId\" = $1 LIMIT 1", [portfolioId]);
        if (snap && snap.length > 0) {
            const entries = Object.entries(secrets).filter(([,v]) => v !== undefined);
            if (entries.length > 0) {
                const setClause = entries.map((_, i) => `"${entries[i][0]}" = $${i + 1}`).join(", ");
                const values = entries.map(e => e[1]);
                values.push(snap[0].id);
                await synczbase.query(`UPDATE portfolio_secrets SET ${setClause} WHERE id = $${values.length}`, values);
            }
        } else {
            const id = `sec_${Date.now()}`;
            await synczbase.query(
                "INSERT INTO portfolio_secrets (id, data) VALUES ($1, $2)",
                [id, JSON.stringify({ portfolioId, ...secrets })]
            );
        }
    }
};

export const uploadPortfolioLogo = async (portfolioId: string, file: File): Promise<string> => {
    const path = `portfolios/${portfolioId}/logo_${Date.now()}_${file.name}`;
    try {
        const res = await synczbase.uploadFile(file, portfolioId);
        return res.url;
    } catch(e) {
        // Fallback to default upload delivery bucket if needed
        return uploadDeliveryFile(file, path);
    }
};

export const uploadBrandAsset = async (portfolioId: string, file: File): Promise<string> => {
    const path = `portfolios/${portfolioId}/brands/${Date.now()}_${file.name}`;
    try {
        const res = await synczbase.uploadFile(file, portfolioId);
        return res.url;
    } catch(e) {
        return uploadDeliveryFile(file, path);
    }
};

// --- TEAM MANAGEMENT ---

export const getPortfolioTeam = async (portfolioId: string): Promise<PortfolioTeamMember[]> => {
    const snap = await synczbase.query("SELECT * FROM portfolio_team WHERE \"portfolioId\" = $1", [portfolioId]);
    return (snap || []) as PortfolioTeamMember[];
};

export const inviteTeamManager = async (portfolioId: string, email: string, name: string) => {
    const currentTeam = await getPortfolioTeam(portfolioId);
    if (currentTeam.filter(m => m.status === 'ACTIVE' || m.status === 'INVITED').length >= 3) {
        throw new Error("Team limit reached (Max 3).");
    }

    const payload = {
        portfolioId,
        email,
        name,
        role: 'MANAGER',
        permissions: ['calendar', 'bookings', 'orders'],
        status: 'INVITED',
        invitedAt: Date.now()
    };
    
    await synczbase.query(
        "INSERT INTO portfolio_team (data) VALUES ($1)",
        [JSON.stringify(payload)]
    );
};

export const removeTeamMember = async (teamMemberId: string) => {
    await synczbase.query("DELETE FROM portfolio_team WHERE id = $1", [teamMemberId]);
};

export const suspendTeamMember = async (teamMemberId: string, suspend: boolean) => {
    const status = suspend ? 'SUSPENDED' : 'ACTIVE';
    await synczbase.query(`UPDATE portfolio_team SET status = $1 WHERE id = $2`, [status, teamMemberId]);
};
